import React from "react";
import { XFormAndLayoutDesignPanel } from "./XFormAndLayoutDesignPanel";
import { FormConfiguration } from "../FormConfiguration";
import { getFormConfig } from "../Testing/FormTestData";
import { FormMode } from "./FormDesignConstants";
import {
    XColumnContainer,
    XColumnContainerColumn,
    XHStackContainer,
    XStackContainer,
    XTabContainer,
    XTabContainerTab,
    XTabContainerTabContent,
    XTabContainerTabHeader,
} from "../Containers";
import { TextFormControl } from "../Controls/Web/TextFormControl";
import { ValidationIssue, ValidationLevel } from "../../rule-engine-core/Common/IDesignValidationProvider";
import { FormContext } from "./FormContext";
import { DefaultOnOff } from "./DefaultOnOff";
import { StoryNode } from "../../CommonUI/StoryBook/StoryData";

export const FormAndLayoutDesignPanelStories = [
    new StoryNode(
        () => {
            let form = getFormConfig();
            let ui = new FormContext(form);
            form.setFormContext(ui);
            ui.mode = FormMode.LayoutDesign;

            const ref = React.createRef<XFormAndLayoutDesignPanel>();
            // hook to the update
            ui.onDesignerUpdate = () => {
                ref.current?.forceUpdate();
            };

            return (
                <XFormAndLayoutDesignPanel
                    editContext={ui}
                    ref={ref}
                />
            );
        },
        "Form Builder",
        "Designer",
        "Pieces",
        "Design Panel ",
    ),
    new StoryNode(
        () => {
            let form = getFormConfig();
            let ui = new FormContext(form);
            form.setFormContext(ui);
            ui.mode = FormMode.LayoutDesign;

            const ref = React.createRef<XFormAndLayoutDesignPanel>();
            // hook to the update
            ui.onDesignerUpdate = () => {
                ref.current?.forceUpdate();
            };

            return (
                <XFormAndLayoutDesignPanel
                    editContext={ui}
                    ref={ref}
                />
            );
        },
        "Form Builder",
        "Designer",
        "Pieces",
        "Design Panel Only Control Mode",
    ),
    new StoryNode(
        () => {
            let form1 = new FormConfiguration();

            let stack = new XStackContainer();
            let errorField = new TextFormControl();
            errorField.label = "Label";
            stack.add(errorField);

            let textField = new TextFormControl();
            textField.valueName = "ttt";
            textField.label = "Text";
            stack.add(textField);

            form1.add(stack);

            let ui = new FormContext(form1);
            form1.setFormContext(ui);

            ui.designValidationProvider = {
                getDesignValidationIssues: (): ValidationIssue[] => {
                    let breaks: ValidationIssue[] = [];
                    for (let c of form1.getAllControls()) {
                        const valueName = Reflect.get(c, "valueName");
                        if (valueName && valueName.length < 6) {
                            breaks.push(
                                new ValidationIssue(
                                    "valueName must have at least 6 characters",
                                    valueName,
                                    c.id,
                                    ValidationLevel.WARNING,
                                ),
                            );
                        }
                    }
                    return breaks;
                },
            };

            // todo: remove these parts, we need to listen to all compoments
            ui.computeDesignValidationIssues();

            const ref = React.createRef<XFormAndLayoutDesignPanel>();
            // hook to the update
            ui.onDesignerUpdate = () => {
                ref.current?.forceUpdate();
            };

            return (
                <XFormAndLayoutDesignPanel
                    editContext={ui}
                    ref={ref}
                />
            );
        },
        "Form Builder",
        "Designer",
        "Pieces",
        "Design Panel With Design Time Validation",
    ),
    new StoryNode(
        () => {
            let form = getFormConfig();
            form.doNotScrollFirstContainerOnForm = true;
            form.doNotScrollLastContainerOnForm = true;

            let ui = new FormContext(form);
            form.setFormContext(ui);

            const ref = React.createRef<XFormAndLayoutDesignPanel>();
            // hook to the update
            ui.onDesignerUpdate = () => {
                ref.current?.forceUpdate();
            };

            return (
                <XFormAndLayoutDesignPanel
                    editContext={ui}
                    ref={ref}
                />
            );
        },
        "Form Builder",
        "Designer",
        "Pieces",
        "Design Panel With Pinned Section",
    ),
    new StoryNode(
        () => {
            let form = new FormConfiguration();
            let stack = new XStackContainer();
            form.add(stack);
            let hstack = new XHStackContainer();
            form.add(hstack);

            let columnContainer = new XColumnContainer();
            form.add(columnContainer);

            let ui = new FormContext(form);
            form.setFormContext(ui);

            const ref = React.createRef<XFormAndLayoutDesignPanel>();
            // hook to the update
            ui.onDesignerUpdate = () => {
                ref.current?.forceUpdate();
            };

            return (
                <XFormAndLayoutDesignPanel
                    editContext={ui}
                    ref={ref}
                />
            );
        },
        "Form Builder",
        "Designer",
        "Pieces",
        "Design Panel with Empty Container",
    ),
    new StoryNode(
        () => {
            let form = new FormConfiguration();

            let stack = new XStackContainer();
            form.add(stack);

            let hstack = new XHStackContainer();
            form.add(hstack);

            let columnContainer = new XColumnContainer();
            form.add(columnContainer);

            let ui = new FormContext(form);
            ui.mode = FormMode.LayoutDesign;
            form.setFormContext(ui);

            const ref = React.createRef<XFormAndLayoutDesignPanel>();
            // hook to the update
            ui.onDesignerUpdate = () => {
                ref.current?.forceUpdate();
            };

            return (
                <XFormAndLayoutDesignPanel
                    editContext={ui}
                    ref={ref}
                />
            );
        },
        "Form Builder",
        "Designer",
        "Pieces",
        "Layout Mode Wtih Empty Container",
    ),
    new StoryNode(
        () => {
            let form = new FormConfiguration();
            let columnContainer = new XColumnContainer();

            let actualCol = new XColumnContainerColumn();
            let actualCol2 = new XColumnContainerColumn();
            let actualCol3 = new XColumnContainerColumn();
            actualCol.add(new TextFormControl());
            actualCol2.add(new TextFormControl());
            actualCol3.add(new TextFormControl());

            columnContainer.add(actualCol);
            columnContainer.add(actualCol2);
            columnContainer.add(actualCol3);

            actualCol.targetWidth = 30;
            actualCol.minSizePx = 500;
            actualCol2.targetWidth = 70;
            actualCol2.minSizePx = 300;
            actualCol3.targetWidth = 20;
            actualCol3.minSizePx = 100;

            columnContainer.lineBetweenColumns = true;
            columnContainer.lineWidthBetweenColumns = 2;
            columnContainer.lineColorBetweenColumns = "gray";

            form.add(columnContainer);

            let ui = new FormContext(form);
            ui.mode = FormMode.LayoutDesign;
            form.setFormContext(ui);

            const ref = React.createRef<XFormAndLayoutDesignPanel>();
            // hook to the update
            ui.onDesignerUpdate = () => {
                ref.current?.forceUpdate();
            };

            return (
                <XFormAndLayoutDesignPanel
                    editContext={ui}
                    ref={ref}
                />
            );
        },
        "Form Builder",
        "Designer",
        "Pieces",
        "Design Panel With Line Between and Overlap",
    ),
    new StoryNode(
        () => {
            let tabContainer = new XTabContainer();

            let tabContainerTab = new XTabContainerTab();

            tabContainer.overrideFormBorderSettings = DefaultOnOff.On;

            let tabContainerTabContent = new XTabContainerTabContent();
            let tabContainerTabHeader = new XTabContainerTabHeader();

            tabContainerTabHeader.title = "Test 1";
            tabContainerTab.setContent(tabContainerTabContent);
            tabContainerTab.setHeader(tabContainerTabHeader);

            let tabContainerTab2 = new XTabContainerTab();
            let tabContainerTabContent2 = new XTabContainerTabContent();
            let tabContainerTabHeader2 = new XTabContainerTabHeader();
            tabContainerTabHeader2.title = "Test 2";
            tabContainerTab2.setContent(tabContainerTabContent2);
            tabContainerTab2.setHeader(tabContainerTabHeader2);

            tabContainer.addTab(tabContainerTab);
            tabContainer.addTab(tabContainerTab2);

            let form = new FormConfiguration();

            form.add(tabContainer);

            let ui = new FormContext(form);
            ui.mode = FormMode.LayoutDesign;
            form.setFormContext(ui);

            const ref = React.createRef<XFormAndLayoutDesignPanel>();
            // hook to the update
            ui.onDesignerUpdate = () => {
                ref.current?.forceUpdate();
            };

            return (
                <XFormAndLayoutDesignPanel
                    editContext={ui}
                    ref={ref}
                />
            );
        },
        "Form Builder",
        "Designer",
        "Pieces",
        "Design Panel With Tab Container",
    ),
];
